/**
 * What a person may attach to a message, read the same by the client's picker and the server's checks (owner
 * 2026-11-01: "most uploads will be prewritten specs + images (charts etc)").
 *
 * Images are what the models look at (PNG, JPEG, WebP, GIF, at most 5 MB: the providers' own limit); documents are what
 * they read as text (PDF and Word up to 20 MB, Markdown and plain text up to 2 MB). Not taken, though erp takes them: an
 * old `.doc` and HEIC — no model here reads them — and SVG, which can carry script.
 *
 * A file is what its bytes say it is: the server checks the declared type against the file's opening bytes
 * (`matchesSignature`) before it is used.
 */
/** What an upload is to a model: an `image` it looks at, or a `document` whose text it reads. */
export const UPLOAD_KINDS = ['image', 'document'] as const;
export type UploadKind = (typeof UPLOAD_KINDS)[number];

const MB = 1024 * 1024;

export interface UploadRule {
  kind: UploadKind;
  maxBytes: number;
  /** How the person knows it ("PNG image"). */
  label: string;
  extensions: readonly string[];
}

export const DOCX_MEDIA_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** The image types: a model that sees images is shown the file itself. */
export const IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export type ImageMediaType = (typeof IMAGE_MEDIA_TYPES)[number];

/** Every type an upload may be (`uploads.media_type`). */
export const UPLOAD_MEDIA_TYPES = [
  ...IMAGE_MEDIA_TYPES,
  'application/pdf',
  DOCX_MEDIA_TYPE,
  'text/markdown',
  'text/plain',
] as const;
export type UploadMediaType = (typeof UPLOAD_MEDIA_TYPES)[number];

export function isUploadMediaType(value: string): value is UploadMediaType {
  return (UPLOAD_MEDIA_TYPES as readonly string[]).includes(value);
}

export function isImageMediaType(value: string): value is ImageMediaType {
  return (IMAGE_MEDIA_TYPES as readonly string[]).includes(value);
}

export const UPLOAD_RULES: Readonly<Record<UploadMediaType, UploadRule>> = {
  'image/png': { kind: 'image', maxBytes: 5 * MB, label: 'PNG image', extensions: ['.png'] },
  'image/jpeg': { kind: 'image', maxBytes: 5 * MB, label: 'JPEG image', extensions: ['.jpg', '.jpeg'] },
  'image/webp': { kind: 'image', maxBytes: 5 * MB, label: 'WebP image', extensions: ['.webp'] },
  'image/gif': { kind: 'image', maxBytes: 5 * MB, label: 'GIF image', extensions: ['.gif'] },
  'application/pdf': { kind: 'document', maxBytes: 20 * MB, label: 'PDF', extensions: ['.pdf'] },
  [DOCX_MEDIA_TYPE]: { kind: 'document', maxBytes: 20 * MB, label: 'Word document', extensions: ['.docx'] },
  'text/markdown': { kind: 'document', maxBytes: 2 * MB, label: 'Markdown', extensions: ['.md', '.markdown'] },
  'text/plain': { kind: 'document', maxBytes: 2 * MB, label: 'Text file', extensions: ['.txt'] },
};

/** What a message can carry, as a person is told when a file is refused for its type (server and client alike). */
export const ATTACHABLE_FILES = 'Attach a PNG, JPEG, WebP or GIF image, or a PDF, Word, Markdown or text document.';

/** How many files one message may carry. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/** How long a presigned PUT stays valid (erp: 15 minutes). */
export const UPLOAD_URL_TTL_SECONDS = 900;

/** Every extension and type the picker offers. */
export const UPLOAD_ACCEPT: readonly string[] = [
  ...Object.keys(UPLOAD_RULES),
  ...Object.values(UPLOAD_RULES).flatMap((rule) => rule.extensions),
];

/** Whether an upload of this type is an image or a document (its type decides; nothing stores it). */
export function uploadKindOf(mediaType: UploadMediaType): UploadKind {
  return UPLOAD_RULES[mediaType].kind;
}

export function uploadRuleFor(mediaType: string): UploadRule | null {
  return isUploadMediaType(mediaType) ? UPLOAD_RULES[mediaType] : null;
}

/**
 * The type to declare for a picked file. A browser often names no type for Markdown (or a generic one), so a file whose
 * own type is not one we take is read by its extension.
 */
export function mediaTypeForFile(fileName: string, browserType: string): UploadMediaType | null {
  if (isUploadMediaType(browserType)) return browserType;
  const name = fileName.toLowerCase();
  return (
    UPLOAD_MEDIA_TYPES.find((mediaType) => UPLOAD_RULES[mediaType].extensions.some((ext) => name.endsWith(ext))) ?? null
  );
}

const ascii = (text: string) => Array.from(text, (char) => char.charCodeAt(0));

function startsWith(bytes: Uint8Array, prefix: readonly number[], offset = 0): boolean {
  return prefix.every((byte, index) => bytes[offset + index] === byte);
}

/** How many opening bytes `matchesSignature` needs. */
export const SIGNATURE_BYTES = 16;

/**
 * Whether the file's opening bytes are the declared type's. Text has no signature: it is checked whole as UTF-8 when
 * its text is read (`decodeUploadText`).
 */
export function matchesSignature(mediaType: string, opening: Uint8Array): boolean {
  switch (mediaType) {
    case 'image/png':
      return startsWith(opening, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case 'image/jpeg':
      return startsWith(opening, [0xff, 0xd8, 0xff]);
    case 'image/gif':
      return startsWith(opening, ascii('GIF87a')) || startsWith(opening, ascii('GIF89a'));
    case 'image/webp':
      return startsWith(opening, ascii('RIFF')) && startsWith(opening, ascii('WEBP'), 8);
    case 'application/pdf':
      return startsWith(opening, ascii('%PDF-'));
    case DOCX_MEDIA_TYPE:
      return startsWith(opening, [0x50, 0x4b, 0x03, 0x04]);
    case 'text/markdown':
    case 'text/plain':
      return true;
    default:
      return false;
  }
}

/** A text file's content when it is UTF-8 text (no NUL bytes); null when it is not. */
export function decodeUploadText(bytes: Uint8Array): string | null {
  if (bytes.includes(0)) return null;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}
